import cn from 'clsx';

type Props = {
  size?: 'sm' | 'md' | 'lg';
  src: string;
  alt: string;
  className?: string;
};

/**
 * One of the covers floating around the hero headline. Its ParallaxLayer both drifts it
 * and supplies the box these offsets are measured from.
 *
 * Decorative. The class sizes the positioned box and the cover fills it, but never grows
 * past its own artwork — which is what next/image's intrinsic layout did at the source.
 * From `md` up the box is wider than the art (208px of box around a 120px cover at `lg`),
 * so without the cap each cover renders three-quarters again too large and the biggest of
 * them sit over the headline. Narrower than the artwork it scales down with the box, as
 * it did there: at 390px the box is `w-20` and the cover comes out 80px in both.
 *
 * headless also carried `short:hidden`, hiding these below a 568px-tall viewport. That
 * breakpoint is deliberately absent from this config — a screen with no `min-width`
 * leaves Tailwind unable to order the breakpoints and it silently stops emitting every
 * `max-*` variant in the tree. See the note in tailwind.config.js.
 */
export default function DigitalMag({ src, alt, size = 'sm', className }: Props) {
  const width = { sm: 120, md: 150, lg: 180 }[size];
  const height = { sm: 159, md: 198, lg: 238 }[size];
  const widthClasses = {
    sm: 'w-20 md:w-44 lg:w-52',
    md: 'w-20 md:w-52 lg:w-56',
    lg: 'w-20 md:w-56 lg:w-60',
  }[size];

  return (
    <span className={cn('absolute drop-shadow-xl hidden', widthClasses, className)}>
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className="w-full h-auto"
        style={{ maxWidth: width }}
      />
    </span>
  );
}
